import { Injectable, inject } from '@angular/core';
import { EMPTY, find, first, Observable, of } from 'rxjs';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { AppUrls } from '../route/app-urls';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { ErrorHandlers } from '../shared/helpers/error-handlers';
import { StampAnnotationColor, TextStampSetting } from '@pdf-tools/four-heights-pdf-web-viewer';
import { StampData } from '../shared/models/stamp-data';
import { StampInfoDto } from '../shared/dto/stamp-info-dto';
import { SymbolGroup } from '../shared/models/symbol-group';
import { WebdokuService } from '../web-doku/webdoku.service';
import { MatDialog } from '@angular/material/dialog';
import { SymbolData } from '../shared/models/symbol-data';
import { SelectSymbolDialogComponent } from './select-symbol-dialog/select-symbol-dialog.component';
import { Photo } from '../shared/models/photo';
import { CapturePhotoDialogComponent } from './capture-photo-dialog/capture-photo-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class AnnotationService {
  private http = inject(HttpClient);
  private webDokuService = inject(WebdokuService);
  private dialog = inject(MatDialog);

  private stampCache$!: Observable<StampInfoDto[]>;

  get allStamps$(): Observable<TextStampSetting[]> {
    if (!this.stampCache$) {
      this.stampCache$ = this.getStampList();
    }
    return this.stampCache$.pipe(
      map((infoList) => (infoList || []).map((info) => ({ text: info.id, color: StampAnnotationColor.GREEN })))
    );
  }

  private getStampList(): Observable<StampInfoDto[]> {
    return this.http.get<StampInfoDto[]>(AppUrls.getApiUrl(AppUrls.urlAnnotation, 'getstamps')).pipe(
      shareReplay(1),
      catchError((error) => {
        return ErrorHandlers.errorHandler(error);
      })
    );
  }

  public getStampAspectRatio(stampId: string): Observable<number> {
    if (!this.stampCache$) {
      this.stampCache$ = this.getStampList();
    }

    return this.stampCache$.pipe(
      map((dtos) => dtos.find((dto) => dto.id == stampId)),
      map((d) => (d ? d.width / d.height : 1.0))
    );
  }

  public getStamp(stampId: string): Observable<StampData> {
    return this.http
      .get<StampData>(AppUrls.getApiUrl(AppUrls.urlAnnotation, 'getstamp') + '?id=' + encodeURIComponent(stampId))
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        catchError((error) => {
          if (error.status === HttpStatusCode.NoContent) {
            return EMPTY;
          }
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  public selectSymbol(): Observable<SymbolData | undefined> {
    return this.http
      .get<SymbolGroup[]>(
        AppUrls.getApiUrl(AppUrls.urlWebDoku, 'getsymbols') + '?webDokuId=' + this.webDokuService.currentWebDokuId
      )
      .pipe(
        catchError((error) => {
          if (error.status === HttpStatusCode.NoContent) {
            return EMPTY;
          }
          return ErrorHandlers.errorHandler(error);
        }),
        switchMap((symbols) =>
          this.dialog
            .open<SelectSymbolDialogComponent, SymbolGroup[], SymbolData>(SelectSymbolDialogComponent, {
              data: symbols,
              width: '100%',
              maxWidth: '1550px',
              minHeight: '540px',
            })
            .afterClosed()
        )
      );
  }

  public captureWebcamImage(): Observable<Photo | undefined> {
    return this.dialog
      .open<CapturePhotoDialogComponent, undefined, Photo>(CapturePhotoDialogComponent, { maxWidth: 700 })
      .afterClosed();
  }
}
